import { useState } from 'react';
import type { BillingStatementStatus, InternalBillingStatement } from '@nadoutong/contracts';
import { useBillingStatements } from '../api/billingStatements.ts';
import { DualCurrencyAmount } from '../components/DualCurrencyAmount.tsx';
import { CustomerRef } from '../components/identity/CustomerRef.tsx';
import { OpsUserRef } from '../components/identity/OpsUserRef.tsx';
import { coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import {
  Button,
  Checkbox,
  Chip,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Panel,
  Select,
  Table,
  TableEmpty,
  TableSkeleton,
  TBody,
  Td,
  Th,
  THead,
  Tr,
  type ChipTone,
} from '../ui/index.ts';

/**
 * Billing-statement read surface (epic #1033 E4, #1956).
 *
 * Lists `billing_statements` rows for admin / customer-manager /
 * finance-manager, filterable by 客户 (account) / 状态 / 逾期. Read-only —
 * there is no write path on this page at all.
 *
 * TWO THINGS THIS PAGE DELIBERATELY DOES NOT DO:
 *
 *  1. It does NOT decide what "逾期" means. The checkbox sets `overdue=true`
 *     on the request and the BFF answers it from the dunning feed, which
 *     applies the C5 live-debt suppression (an account whose current debt
 *     position is zero is not overdue even while its `open` rows remain).
 *     Comparing `due_date` to today here would flag customers who owe nothing
 *     — the page would say "逾期" about someone the system will never dun.
 *
 *  2. It does NOT show `accounts.billing_cycle_days`. That leftover column
 *     is no longer written on approve (router#1999); payment terms live on
 *     `billing_grace_days` and already appear here as `due_date`.
 *
 * Money is integer micro-USD on the wire; formatting happens here and only
 * here. `cny_reference_closing_ar` is a CNY-cent REFERENCE captured at the
 * issuance-time policy rate — shown beside the USD figure, never instead of it.
 */

const PAGE_LIMIT = 50;

const STATUS_CHIP_TONE: Record<BillingStatementStatus, ChipTone> = {
  open: 'warn',
  closed: 'ok',
};

const STATUS_LABEL_KEYS: Record<BillingStatementStatus, I18nKey> = {
  open: 'billing_statements.status.open',
  closed: 'billing_statements.status.closed',
};

export function BillingStatementsPage(): JSX.Element {
  const t = useT();
  const [accountInput, setAccountInput] = useState('');
  const [status, setStatus] = useState<BillingStatementStatus | ''>('');
  const [overdue, setOverdue] = useState(false);

  const query = useBillingStatements({
    ...(accountInput.trim() === '' ? {} : { accountId: accountInput.trim() }),
    ...(status === '' ? {} : { status }),
    ...(overdue ? { overdue: true } : {}),
    limit: PAGE_LIMIT,
  });

  const rows: InternalBillingStatement[] =
    query.data?.pages.flatMap((page) => page.items) ?? [];

  const statusOptions = [
    { value: 'all', label: t('billing_statements.status.all') },
    { value: 'open', label: t('billing_statements.status.open') },
    { value: 'closed', label: t('billing_statements.status.closed') },
  ];

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('billing_statements.title')}
        sub={t('billing_statements.subtitle')}
        testId="billing-statements-header"
      />

      <Panel
        caption={t('billing_statements.title')}
        count={rows.length}
        testId="billing-statements-panel"
      >
        <div className="flex flex-wrap items-end gap-3 border-b border-line-soft px-4 py-3">
          <FormField label={t('billing_statements.filter.account')} fieldId="bs-filter-account">
            <Input
              id="bs-filter-account"
              value={accountInput}
              onChange={(e) => setAccountInput(e.target.value)}
              placeholder={t('billing_statements.filter.account_placeholder')}
              className="w-64"
              data-testid="billing-statements-filter-account"
            />
          </FormField>
          <FormField label={t('billing_statements.filter.status')} fieldId="bs-filter-status">
            <Select
              value={status === '' ? 'all' : status}
              onValueChange={(value) =>
                setStatus(value === 'all' ? '' : (value as BillingStatementStatus))
              }
              options={statusOptions}
              ariaLabel={t('billing_statements.filter.status')}
              testId="billing-statements-filter-status"
            />
          </FormField>
          <Checkbox
            checked={overdue}
            onCheckedChange={setOverdue}
            label={t('billing_statements.filter.overdue')}
            testId="billing-statements-filter-overdue"
            className="pb-2"
          />
        </div>

        {query.isPending ? <TableSkeleton rows={4} testId="billing-statements-loading" /> : null}
        {query.isError ? (
          <EmptyState
            title={t('billing_statements.load_error')}
            hint={coerceErrorMessage(query.error)}
            action={<Button onClick={() => void query.refetch()}>{t('common.retry')}</Button>}
            testId="billing-statements-error"
          />
        ) : null}
        {query.isSuccess ? (
          <>
            <Table>
              <THead>
                <Tr interactive={false}>
                  <Th>{t('billing_statements.col.account')}</Th>
                  <Th>{t('billing_statements.col.period')}</Th>
                  <Th num>{t('billing_statements.col.closing_ar')}</Th>
                  <Th>{t('billing_statements.col.due_date')}</Th>
                  <Th>{t('billing_statements.col.status')}</Th>
                  <Th>{t('billing_statements.col.owner')}</Th>
                </Tr>
              </THead>
              <TBody>
                {rows.length === 0 ? (
                  <TableEmpty colSpan={6} testId="billing-statements-empty">
                    {t('billing_statements.empty')}
                  </TableEmpty>
                ) : (
                  rows.map((row) => (
                    <StatementRow key={row.id} row={row} unassignedLabel={t('billing_statements.owner.unassigned')} statusLabel={t(STATUS_LABEL_KEYS[row.status])} />
                  ))
                )}
              </TBody>
            </Table>
            {query.hasNextPage ? (
              <div className="border-t border-line-soft px-4 py-3">
                <Button
                  variant="ghost"
                  disabled={query.isFetchingNextPage}
                  onClick={() => void query.fetchNextPage()}
                  data-testid="billing-statements-load-more"
                >
                  {query.isFetchingNextPage
                    ? t('common.loading')
                    : t('billing_statements.load_more')}
                </Button>
              </div>
            ) : null}
          </>
        ) : null}
      </Panel>
    </div>
  );
}

function StatementRow({
  row,
  statusLabel,
  unassignedLabel,
}: {
  readonly row: InternalBillingStatement;
  readonly statusLabel: string;
  readonly unassignedLabel: string;
}): JSX.Element {
  return (
    <Tr interactive={false} data-testid={`billing-statement-row-${row.id}`}>
      <Td mono>
        <CustomerRef accountId={row.account_id} />
      </Td>
      <Td mono>{`${row.statement_period_start} → ${row.statement_period_end}`}</Td>
      <Td num>
        <DualCurrencyAmount
          usdMicros={row.closing_ar_usd_micros}
          cnyCents={row.cny_reference_closing_ar}
          signed={false}
          usdTestId={`billing-statement-ar-usd-${row.id}`}
          cnyTestId={`billing-statement-ar-cny-${row.id}`}
        />
      </Td>
      <Td mono>{row.due_date}</Td>
      <Td>
        <Chip tone={STATUS_CHIP_TONE[row.status]}>{statusLabel}</Chip>
      </Td>
      <Td mono>
        {row.owner_ops_user_id === null ? unassignedLabel : <OpsUserRef id={row.owner_ops_user_id} />}
      </Td>
    </Tr>
  );
}
